import React from "react";

import dayjs from "dayjs";

import { CircularProgress, Stack, Table} from "@mui/joy";
import Typography from "@mui/joy/Typography";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";

// components
import BaseApi from "../../utils/BaseApi";
import UserSubscriberContext from "../../context/UserSubscriberContext";
import CommonPaginatedTable from "../Common/CommonPaginatedTable";
import {colors} from "../Calculator/Calculator";
import ProfileHorizontalByProps from "../Profile/ProfileHorizontalByProps";
import OpportunityEvaluationTaskModal from "./OpportunityEvaluationTaskModal";
import EstimationPhaseCompleted from "../Estimation/EstimationPhaseCompleted";
import retrievePermission from "../../utils/retrievePermission";


class OpportunityEvaluation extends React.Component {
    static contextType = UserSubscriberContext

    constructor(props) {
        super(props);
        this.state = {
            phases: null,
            charts: null,
            tasks: null,
            team: null,
            reeport: null,
            ready: false,
            estimationPhaseUpdatePermission: 0,
        };
        this.year = (new Date()).getFullYear()
        this.colors = colors
    }

    async componentDidMount() {
        await this.retrieveReport();
    }


    async retrieveReport(){
        this.setState(
            {
                phases: null,
                ready: false,
            }
        );
        let estimationPhaseUpdatePermission = await retrievePermission(
            'EstimationPhaseViewSet',
            'partial_update',
            this.context.subscriberObject
        );
        const api = new BaseApi();
        let phases = [];
        let charts = [];
        let url = `opportunity/${this.props.opportunity.id}/evaluation/`;
        let response = await api.get(url, {});
        let report = response.data
        url = `estimation_revision/`;
        let params = {
            opportunity: this.props.opportunity.id,
            current: true,
        };
        response = await api.get(
            url,
            params
        );
        if (response.status === 200){
            for (let revision of response.data.results){
                url = `estimation_revision/${revision.id}/chart`;
                response = await api.get(
                    url,
                    {}
                );
                charts.push(response.data)
                url = `estimation_phase/`;
                params = {
                    is_active: true,
                    revision_fk: revision.id,
                    page_size: 10000,
                };
                response = await api.get(
                    url,
                    params
                );
                phases = [...phases, ...response.data.results]
            }
        }
        for (let phase of phases){
            phase.estimation_normal = phase.estimation_normal * 8;
            phase.estimation_minimum = phase.estimation_minimum * 8;
            phase.estimation_maximum = phase.estimation_maximum * 8;
        }
        url = 'task';
        params = {
            opportunity: this.props.opportunity.id,
            page_size: 10000,
        };
        response = await api.get(
            url,
            params
        );
        let tasks = response.data.results
        let taskOwners = {}
        let taskYears = []
        let phaseTasks = {}
        for (let task of tasks){
            taskOwners[task.owner.id] = task.owner
            taskYears.push(dayjs(task.start_dt).year())
            if (task.phase){
                if (task.phase.id in phaseTasks){
                    phaseTasks[task.phase.id].push(task)
                }
                else {
                    phaseTasks[task.phase.id] = [task]
                }
            }
        }
        taskYears = [...new Set(taskYears)]
        let durationCoef = {
            'PSD': 0,
            'DCL': 0,
            'WRK': 1,
            'OAC': 1,
            'COM': 1,
            'CLD': 0,
            'DEL': 0,
        }
        for (let phase of phases){
            phase.durationPlan = 0;
            phase.durationFact = 0;
            phase.toGoWasted = 0;
            if (phase.id in phaseTasks) {
                phase.durationPlan = phaseTasks[phase.id].reduce(
                    (accumulator, task) => accumulator + task.duration_plan * durationCoef[task.work_status], 0
                );
                phase.durationFact = phaseTasks[phase.id].reduce(
                    (accumulator, task) => accumulator + task.duration_fact * durationCoef[task.work_status] * task.complete / 100, 0
                );
            }
            if (phase.completed) {
                phase.toGo = 0;
                let delta = phase.estimation_normal - phase.durationFact
                if (delta >= 0) {
                    phase.deltaSaved = delta;
                    phase.deltaWasted = 0;
                } else {
                    phase.deltaSaved = 0;
                    phase.deltaWasted = delta * -1;
                }
            }
            else {
                phase.toGo = phase.estimation_normal - phase.durationFact;
                phase.deltaSaved = 0;
                phase.deltaWasted = 0;
            }
            if (phase.toGo < 0){
                phase.toGoWasted = phase.toGo * -1;
                phase.deltaWasted = phase.toGo * -1;
                phase.toGo = 0;
            }
        }
        this.setState(
            {
                phases: phases,
                charts: charts,
                tasks: tasks,
                taskOwners: taskOwners,
                taskYears: taskYears,
                report: report,
                estimationPhaseUpdatePermission: estimationPhaseUpdatePermission,
                ready: true,
            }
        );
    }

    render() {
        if (!this.state.ready) {
            return (<CircularProgress/>)
        }
        let yearColors = {}
        for (let year of this.state.taskYears){
            yearColors[year] = this.colors[Math.floor(Math.random() * this.colors.length)]
        }
        return (
            <Stack
                spacing={2}
            >
                <Card>
                    <CardContent>
                        <Typography
                            level={'body-lg'}
                            sx={{
                                mb: 1
                            }}
                        >
                            Соответствие плану
                        </Typography>
                        <CommonPaginatedTable
                            data={this.state.phases}
                            summary={[
                                {
                                    title: 'Норма - оценка',
                                    name: 'estimation_normal',
                                    color: 'primary',
                                    visible: true,
                                },
                                {
                                    title: 'План',
                                    name: 'durationPlan',
                                    color: 'primary',
                                    visible: true,
                                },
                                {
                                    title: 'Факт',
                                    name: 'durationFact',
                                    color: 'primary',
                                    visible: true,
                                },
                                {
                                    title: 'Сохранено',
                                    name: 'deltaSaved',
                                    color: 'success',
                                    visible: true,
                                },
                                {
                                    title: 'Переработка',
                                    name: 'deltaWasted',
                                    color: 'danger',
                                    visible: true,
                                },
                                {
                                    title: 'Впереди',
                                    name: 'toGo',
                                    color: 'pink',
                                    visible: true,
                                },
                                {
                                    title: 'Переработка впереди',
                                    name: 'toGoWasted',
                                    color: 'pink',
                                    visible: true,
                                },
                                {
                                    title: 'Общая оценка',
                                    name: ['durationFact', 'toGo'],
                                    color: 'indigo',
                                    visible: true,
                                },
                            ]}
                            fields={[
                                {
                                    title: 'Рев.',
                                    name: 'revision_fk.id',
                                    visible: true,
                                },
                                {
                                    title: '#',
                                    name: 'id',
                                    visible: true,
                                },
                                {
                                    title: 'Название',
                                    name: 'title',
                                    visible: true,
                                    width: '30%',
                                },
                                {
                                    title: 'Мин',
                                    name: 'estimation_minimum',
                                    visible: true,
                                },
                                {
                                    title: 'Норм',
                                    name: 'estimation_normal',
                                    visible: true,
                                },
                                {
                                    title: 'Макс',
                                    name: 'estimation_maximum',
                                    visible: true,
                                },
                                {
                                    title: 'План',
                                    name: 'durationPlan',
                                    visible: true,
                                },
                                {
                                    title: 'Факт',
                                    name: 'durationFact',
                                    visible: true,

                                },
                                {
                                    title: 'Сохранено',
                                    name: 'deltaSaved',
                                    visible: true,
                                    chip: true,
                                    color_value: 'deltaSaved',
                                    color_rule: (value, comparison) => {
                                        if (value > 0){
                                            return 'success'
                                        }
                                        else {
                                            return 'grey'
                                        }
                                    },
                                },
                                {
                                    title: 'Переработка',
                                    name: 'deltaWasted',
                                    visible: true,
                                    chip: true,
                                    color_value: 'deltaWasted',
                                    color_rule: (value, comparison) => {
                                        if (value > 0){
                                            return 'danger'
                                        }
                                        else {
                                            return 'grey'
                                        }
                                    },
                                },
                                {
                                    title: 'Впереди',
                                    name: 'toGo',
                                    chip: true,
                                    color_value: 'toGo',
                                    color_rule: (value, comparison) => {
                                        console.log(value)
                                        if (value > 0){
                                            return 'pink'
                                        }
                                        else {
                                            return 'grey'
                                        }
                                    },
                                    visible: true,
                                },
                                {
                                    title: 'Задачи',
                                    visible: true,
                                    component: OpportunityEvaluationTaskModal,
                                    componentProps: {},
                                },
                                {
                                    title: 'Завершено',
                                    visible: true,
                                    component: EstimationPhaseCompleted,
                                    componentProps: {
                                        permission: this.state.estimationPhaseUpdatePermission,
                                        callBack: this.retrieveReport.bind(this),
                                    },
                                },
                            ]}
                        />
                    </CardContent>
                </Card>
                <Card>
                    <CardContent>
                        <Typography
                            level={'body-lg'}
                            sx={{
                                mb: 2
                            }}
                        >
                            Общий обзор
                        </Typography>
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                '--td-headBackground': (theme) =>
                                theme.vars.palette.background.level1,
                                '--Table-headerUnderlineThickness': '1px',
                                '--tr-hoverBackground': (theme) =>
                                theme.vars.palette.background.level1,
                            }}
                        >
                            <thead>
                                <tr>
                                    <th>
                                        Сотрудник
                                    </th>
                                    <th>
                                        Загрузка план<br/>(в трудоемости + вне)
                                    </th>
                                    <th>
                                        Загрузка факт<br/>(в трудоемости + вне)
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.report.user_workloads.map((row) => (
                                    <tr key={row.user.id}>
                                        <td>
                                            <ProfileHorizontalByProps
                                                user={row.user}
                                            />
                                        </td>
                                        <td>
                                            {row.workload_plan_estimation} + {row.workload_plan_not_estimation}
                                        </td>
                                        <td>
                                            {row.workload_fact_estimation} + {row.workload_fact_not_estimation}
                                        </td>
                                    </tr>
                                ))}
                                <tr key={'totals'}>
                                    <td>
                                        <strong>Итого</strong>
                                    </td>
                                    <td>
                                        <strong>{this.state.report.total_workloads.workload_plan_estimation} + {this.state.report.total_workloads.workload_plan_not_estimation}</strong>
                                    </td>
                                    <td>
                                        <strong>{this.state.report.total_workloads.workload_fact_estimation} + {this.state.report.total_workloads.workload_fact_not_estimation}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                        <Typography
                            sx={{
                                mt: 2
                            }}
                        >
                            <strong>
                                Ожидаемая трудоемкость: {this.state.report.total_workloads.max_proba_estimation}
                            </strong>
                        </Typography>
                    </CardContent>
                </Card>
                {/*<RenderIfVisible*/}
                {/*    stayRendered={true}*/}
                {/*>*/}
                {/*    <Card>*/}
                {/*        <CardContent>*/}
                {/*            <Typography*/}
                {/*                level={'body-lg'}*/}
                {/*                sx={{*/}
                {/*                    mb: 2*/}
                {/*                }}*/}
                {/*            >*/}
                {/*                Общий обзор (графики)*/}
                {/*            </Typography>*/}
                {/*            <Typography*/}
                {/*                level={'body-md'}*/}
                {/*                sx={{*/}
                {/*                    mb: 2*/}
                {/*                }}*/}
                {/*            >*/}
                {/*                Плановые показатели*/}
                {/*            </Typography>*/}
                {/*            <CalculatorItem*/}
                {/*                key={crypto.randomUUID()}*/}
                {/*                calculatorURL={'calculator/safe_report/'}*/}
                {/*                item={{*/}
                {/*                    charts: [*/}
                {/*                        {*/}
                {/*                            "years": this.state.taskYears,*/}
                {/*                            "dataset": "default",*/}
                {/*                            "colorMode": "list",*/}
                {/*                            "yearColors": yearColors,*/}
                {/*                            "datasetValue": "N/A",*/}
                {/*                            "currentCategory": "duration_plan"*/}
                {/*                        }*/}
                {/*                    ],*/}
                {/*                    size: 6,*/}
                {/*                    queryset_params: JSON.stringify({*/}
                {/*                        "active": "True",*/}
                {/*                        "task__opportunity__id": this.props.opportunity.id,*/}
                {/*                    }),*/}
                {/*                    groups: JSON.stringify([*/}
                {/*                        'task.executor',*/}
                {/*                        'task.phase',*/}
                {/*                    ]),*/}
                {/*                    groupsConfig: {*/}
                {/*                        "task.executor": {*/}
                {/*                            "year": this.state.taskYears.at(-1),*/}
                {/*                            "legendIsHidden": false,*/}
                {/*                            "currentCategory": "duration_plan",*/}
                {/*                        },*/}
                {/*                        "task.phase": {*/}
                {/*                            "year": this.state.taskYears.at(-1),*/}
                {/*                            "legendIsHidden": false,*/}
                {/*                            "currentCategory": "duration_plan",*/}
                {/*                        },*/}
                {/*                    },*/}
                {/*                    year_end: Math.max(...this.state.taskYears),*/}
                {/*                    year_start: Math.min(...this.state.taskYears),*/}
                {/*                    model: 'TaskDailyDuration',*/}
                {/*                    period:	"month",*/}
                {/*                    field: '',*/}
                {/*                    date_field: '',*/}
                {/*                    custom_date_field: 'date',*/}
                {/*                    custom_field: 'duration_plan',*/}
                {/*                }}*/}
                {/*                updateItemCallBack={() => {}}*/}
                {/*                colors={this.colors}*/}
                {/*            />*/}
                {/*            <Typography*/}
                {/*                level={'body-md'}*/}
                {/*                sx={{*/}
                {/*                    mt: 2,*/}
                {/*                    mb: 2*/}
                {/*                }}*/}
                {/*            >*/}
                {/*                Фактические показатели*/}
                {/*            </Typography>*/}
                {/*            <CalculatorItem*/}
                {/*                key={crypto.randomUUID()}*/}
                {/*                calculatorURL={'calculator/safe_report/'}*/}
                {/*                item={{*/}
                {/*                    charts: [*/}
                {/*                        {*/}
                {/*                            "years": this.state.taskYears,*/}
                {/*                            "dataset": "default",*/}
                {/*                            "colorMode": "list",*/}
                {/*                            "yearColors": yearColors,*/}
                {/*                            "datasetValue": "N/A",*/}
                {/*                            "currentCategory": "duration_fact"*/}
                {/*                        }*/}
                {/*                    ],*/}
                {/*                    size: 6,*/}
                {/*                    queryset_params: JSON.stringify({*/}
                {/*                        "active": "True",*/}
                {/*                        "task__opportunity__id": this.props.opportunity.id,*/}
                {/*                    }),*/}
                {/*                    groups: JSON.stringify([*/}
                {/*                        'task.executor',*/}
                {/*                        'task.phase',*/}
                {/*                    ]),*/}
                {/*                    groupsConfig: {*/}
                {/*                        "task.executor": {*/}
                {/*                            "year": this.state.taskYears.at(-1),*/}
                {/*                            "legendIsHidden": false,*/}
                {/*                            "currentCategory": "duration_fact"*/}
                {/*                        },*/}
                {/*                        "task.phase": {*/}
                {/*                            "year": this.state.taskYears.at(-1),*/}
                {/*                            "legendIsHidden": false,*/}
                {/*                            "currentCategory": "duration_fact"*/}
                {/*                        },*/}
                {/*                    },*/}
                {/*                    year_end: Math.max(...this.state.taskYears),*/}
                {/*                    year_start: Math.min(...this.state.taskYears),*/}
                {/*                    model: 'TaskDailyDuration',*/}
                {/*                    period:	"month",*/}
                {/*                    field: '',*/}
                {/*                    date_field: '',*/}
                {/*                    custom_date_field: 'date',*/}
                {/*                    custom_field: 'duration_fact',*/}
                {/*                }}*/}
                {/*                updateItemCallBack={() => {}}*/}
                {/*                colors={this.colors}*/}
                {/*            />*/}
                {/*        </CardContent>*/}
                {/*    </Card>*/}
                {/*</RenderIfVisible>*/}
                {/*<RenderIfVisible*/}
                {/*    stayRendered={true}*/}
                {/*>*/}
                {/*    <Card>*/}
                {/*        <CardContent>*/}
                {/*            <Stack>*/}
                {/*                <Typography*/}
                {/*                    level={'body-lg'}*/}
                {/*                    sx={{*/}
                {/*                        mt: 2,*/}
                {/*                        mb: 2,*/}
                {/*                    }}*/}
                {/*                >*/}
                {/*                    Обзор по исполнителям*/}
                {/*                </Typography>*/}
                {/*                <AccordionGroup*/}
                {/*                    variant="plain"*/}
                {/*                    transition="0.2s"*/}
                {/*                >*/}
                {/*                    {*/}
                {/*                        Object.entries(this.state.taskOwners).map(([owner, ownerDict]) => (*/}
                {/*                            <Accordion>*/}
                {/*                                <AccordionSummary>*/}
                {/*                                    <ProfileHorizontalByProps*/}
                {/*                                        user={ownerDict}*/}
                {/*                                    />*/}
                {/*                                </AccordionSummary>*/}
                {/*                                <AccordionDetails>*/}
                {/*                                    <Typography*/}
                {/*                                        level={'body-md'}*/}
                {/*                                        sx={{*/}
                {/*                                            mb: 2*/}
                {/*                                        }}*/}
                {/*                                    >*/}
                {/*                                        Плановые показатели*/}
                {/*                                    </Typography>*/}
                {/*                                    <CalculatorItem*/}
                {/*                                        key={crypto.randomUUID()}*/}
                {/*                                        calculatorURL={'calculator/safe_report/'}*/}
                {/*                                        item={{*/}
                {/*                                            charts: [*/}
                {/*                                                {*/}
                {/*                                                    "years": this.state.taskYears,*/}
                {/*                                                    "dataset": "default",*/}
                {/*                                                    "colorMode": "list",*/}
                {/*                                                    "yearColors": yearColors,*/}
                {/*                                                    "datasetValue": "N/A",*/}
                {/*                                                    "currentCategory": "duration_plan"*/}
                {/*                                                }*/}
                {/*                                            ],*/}
                {/*                                            size: 12,*/}
                {/*                                            queryset_params: JSON.stringify({*/}
                {/*                                                "active": "True",*/}
                {/*                                                "task__executor__id": owner,*/}
                {/*                                                "task__opportunity__id": this.props.opportunity.id,*/}
                {/*                                            }),*/}
                {/*                                            groups: JSON.stringify([*/}
                {/*                                                'task.phase',*/}
                {/*                                            ]),*/}
                {/*                                            year_end: Math.max(...this.state.taskYears),*/}
                {/*                                            year_start: Math.min(...this.state.taskYears),*/}
                {/*                                            model: 'TaskDailyDuration',*/}
                {/*                                            period:	"month",*/}
                {/*                                            field: '',*/}
                {/*                                            date_field: '',*/}
                {/*                                            custom_date_field: 'date',*/}
                {/*                                            custom_field: 'duration_plan',*/}
                {/*                                        }}*/}
                {/*                                        updateItemCallBack={() => {}}*/}
                {/*                                        colors={this.colors}*/}
                {/*                                    />*/}
                {/*                                    <Typography*/}
                {/*                                        level={'body-md'}*/}
                {/*                                        sx={{*/}
                {/*                                            mt: 2,*/}
                {/*                                            mb: 2*/}
                {/*                                        }}*/}
                {/*                                    >*/}
                {/*                                        Фактические показатели*/}
                {/*                                    </Typography>*/}
                {/*                                    <CalculatorItem*/}
                {/*                                        key={crypto.randomUUID()}*/}
                {/*                                        calculatorURL={'calculator/safe_report/'}*/}
                {/*                                        item={{*/}
                {/*                                            charts: [*/}
                {/*                                                {*/}
                {/*                                                    "years": this.state.taskYears,*/}
                {/*                                                    "dataset": "default",*/}
                {/*                                                    "colorMode": "list",*/}
                {/*                                                    "yearColors": yearColors,*/}
                {/*                                                    "datasetValue": "N/A",*/}
                {/*                                                    "currentCategory": "duration_fact"*/}
                {/*                                                }*/}
                {/*                                            ],*/}
                {/*                                            size: 12,*/}
                {/*                                            queryset_params: JSON.stringify({*/}
                {/*                                                "active": "True",*/}
                {/*                                                "task__executor__id": owner,*/}
                {/*                                                "task__opportunity__id": this.props.opportunity.id,*/}
                {/*                                            }),*/}
                {/*                                            groups: JSON.stringify([*/}
                {/*                                                'task.phase',*/}
                {/*                                            ]),*/}
                {/*                                            year_end: Math.max(...this.state.taskYears),*/}
                {/*                                            year_start: Math.min(...this.state.taskYears),*/}
                {/*                                            model: 'TaskDailyDuration',*/}
                {/*                                            period:	"month",*/}
                {/*                                            field: '',*/}
                {/*                                            date_field: '',*/}
                {/*                                            custom_date_field: 'date',*/}
                {/*                                            custom_field: 'duration_fact',*/}
                {/*                                        }}*/}
                {/*                                        updateItemCallBack={() => {}}*/}
                {/*                                        colors={this.colors}*/}
                {/*                                    />*/}
                {/*                                </AccordionDetails>*/}
                {/*                            </Accordion>*/}
                {/*                        ))*/}
                {/*                    }*/}
                {/*                </AccordionGroup>*/}
                {/*            </Stack>*/}
                {/*        </CardContent>*/}
                {/*    </Card>*/}
                {/*</RenderIfVisible>*/}
            </Stack>
        );
    }
}

export default OpportunityEvaluation
